import { Router } from '~i18n';

/**
 * 已重构完成的页面路由列表
 *
 * 使用说明：
 * 1. 当新页面重构完成后，将路由添加到此列表
 * 2. 新项目和老项目需要保持此列表同步
 * 3. 路由格式：不包含语言前缀，以 / 开头
 */
const MIGRATED_ROUTES = [
  '/login',
  '/forgot-password',
  '/create-account',
  '/set-password',
  '/change-email',
  '/verify-email',
  '/my-bookings',
  '/my-profile',
  '/check-out',
  '/residences',
] as const;

/**
 * 检查路由是否已重构
 */
function isMigratedRoute(path: string): boolean {
  // 移除语言前缀（如 /en-gb/）
  const pathWithoutLang = path.replace(/^\/[a-z]{2}-[a-z]{2}\//, '/');

  // 移除查询参数和 hash
  const cleanPath = pathWithoutLang.split('?')[0].split('#')[0];

  // 检查是否匹配已重构的路由（支持带参数的路由如 /set-password/xxx）
  return MIGRATED_ROUTES.some(route => {
    if (cleanPath === route) return true;
    // 支持 /set-password/:token 这类带参数的路由
    if (cleanPath.startsWith(route + '/')) return true;
    return false;
  });
}

interface UnifiedNavigateOptions {
  replace?: boolean;
}

/**
 * 统一路由跳转（老项目版本）
 *
 * - 已重构路由：使用 window.location 跳转，走 nginx 路由到新项目
 * - 未重构路由：使用 i18n Router 内部跳转
 *
 * @example
 * unifiedNavigate('/login'); // 已重构，跳转到新项目
 * unifiedNavigate('/my-bookings'); // 未重构，内部跳转
 */
export function unifiedNavigate(path: string, options?: UnifiedNavigateOptions) {
  if (isMigratedRoute(path)) {
    // 已重构的路由：使用 window.location 跳转，让 nginx 路由到新项目
    // 需要先构建完整路径（i18n Router 会自动加语言前缀，但 window.location 不会）
    const currentLang =
      window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\//)?.[1] || 'en-gb';
    const fullPath = path.match(/^\/[a-z]{2}(?:-[a-z]{2})?\//)
      ? path
      : `/${currentLang}${path.startsWith('/') ? path : `/${path}`}`;

    if (options?.replace) {
      window.location.replace(fullPath);
    } else {
      window.location.href = fullPath;
    }
  } else {
    // 未重构的路由：使用 i18n Router 内部跳转
    if (options?.replace) {
      Router.replace(path);
    } else {
      Router.push(path);
    }
  }
}

export default unifiedNavigate;
